// Global Instructions Rule Applied!
//
// Connected agents — the lifecycle tab of the My Agents screen (Phase 3
// consent + grant lifecycle, design §7): the table lists MY grants (active +
// revoked history, owner-scoped end-to-end by user_id = auth.uid() in the
// RPCs + RLS). All options are cfg-driven via globalConfigs.agent_access;
// nothing here is hardcoded.
//
// There is deliberately NO "Connect agent" button. A grant is born in exactly
// one place — the gateway's OAuth consent page, collected mid-handshake when
// the user connects from their assistant. The old button only pre-issued the
// grant so that page would be skipped; it generated no URL, started no OAuth,
// and went permanently disabled once a grant existed by either path.
//
// The two row actions are the whole lifecycle:
//   * Edit   — change consented tools, per-write-tool mode, and expiry. Edits
//     the row IN PLACE (the grant id is an FK target for agent_proposals and
//     the key for agent_usage_counters). Offered on expired grants too, where
//     it is the renewal path.
//   * Revoke — the per-user kill switch: no new agent sessions mint and live
//     tokens die within their short TTL.
//
// The "Connect your agent" block that used to sit at the top of this tab now
// has a screen of its own: Configuration -> Connect an Agent
// (ConnectAgentPanel.tsx, owner 2026-09-02). It had to move, because this tab
// is only reachable once you ALREADY have a grant — the menu item carries
// visibility_rule 'has_connected_agent' — so the instructions for getting your
// first one could not live behind it. The section header here links across, so
// a user whose access lapsed still has a route back.

import React, { useMemo, useState } from 'react'
import { Alert, Button, Empty, Popconfirm, Space, Spin, Table, Tag, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useAgentGrants } from '../hooks/useAgentGrants.ts'
import { EditGrantModal } from './EditGrantModal.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { HelpButton } from '../../../core/components/HelpDrawer/HelpButton.tsx'
import {
  AgentAccessConfigSchema,
  type AgentGrant
} from '../../../state_manager/data_models/agent_access_model.ts'
import type { GlobalConfigsState } from '../../../state_manager/interfaces/global_configs.ts'

const { Text } = Typography

const isExpired = (grant: AgentGrant): boolean =>
  dayjs(grant.agent_grants__expires_at).isBefore(dayjs())

const isLiveActive = (grant: AgentGrant): boolean =>
  grant.agent_grants__status === 'active' && !isExpired(grant)

export function MyAgentsTab() {
  const rawAgentAccess = useSelector(
    (state: { globalConfigs: GlobalConfigsState }) => state.globalConfigs?.agent_access
  )
  // Parse-with-defaults so a missing/partial cfg payload degrades to an empty
  // option set (fail-closed: no agents offered) instead of a crash.
  const agentAccess = useMemo(
    () => AgentAccessConfigSchema.parse(rawAgentAccess ?? {}),
    [rawAgentAccess]
  )

  const { grants, loading, savingId, revokingId, updateGrant, revoke } = useAgentGrants()
  const [editing, setEditing] = useState<AgentGrant | null>(null)
  const navigate = useNavigate()

  const toolLabel = useMemo(() => {
    const labels: Record<string, string> = {}
    for (const tool of agentAccess.tools) labels[tool.tool_key] = tool.name || tool.tool_key
    return labels
  }, [agentAccess])

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'agent_grants__agent_name',
      key: 'agent',
      render: (name: string, grant: AgentGrant) => (
        <>
          <Text strong>{name}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {grant.agent_grants__agent_id}
          </Text>
        </>
      )
    },
    {
      // Not "May read": this list has always included write tools too, and
      // Edit is only usable if you can see what you are editing. Live write
      // tools are called out — "acts without asking" is the one distinction
      // in here that carries real risk.
      title: 'Permissions',
      key: 'tools',
      render: (_: unknown, grant: AgentGrant) => {
        const live = new Set(grant.agent_grants__scopes.live_tools || [])
        return (
          <>
            {(grant.agent_grants__scopes.tools || []).map((toolKey) => (
              <Tag key={toolKey} color={live.has(toolKey) ? 'orange' : undefined}>
                {toolLabel[toolKey] || toolKey}
                {live.has(toolKey) ? ' · live' : ''}
              </Tag>
            ))}
          </>
        )
      }
    },
    {
      title: 'Granted',
      dataIndex: 'agent_grants__date_created',
      key: 'granted',
      render: (value: string) => dayjs(value).format('D MMM YYYY')
    },
    {
      title: 'Expires',
      dataIndex: 'agent_grants__expires_at',
      key: 'expires',
      render: (value: string) => dayjs(value).format('D MMM YYYY')
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, grant: AgentGrant) => {
        if (grant.agent_grants__status === 'revoked') return <Tag>Revoked</Tag>
        if (isExpired(grant)) return <Tag color='orange'>Expired</Tag>
        return <Tag color='green'>Active</Tag>
      }
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, grant: AgentGrant) => (
        <Space size={0}>
          {/* Edit is offered on any grant that has not been revoked —
              including an expired one, where changing the date is how access
              is renewed. Revocation is final, so a revoked row has neither
              action. */}
          {grant.agent_grants__status === 'active' ? (
            <Button
              size='small'
              loading={savingId === grant.agent_grants__id}
              onClick={() => setEditing(grant)}
            >
              {isLiveActive(grant) ? 'Edit' : 'Renew'}
            </Button>
          ) : null}
          {isLiveActive(grant) ? (
            <Popconfirm
              title='Revoke this agent’s access?'
              description='No new agent sessions will start; any live session ends within minutes.'
              okText='Revoke'
              okButtonProps={{ danger: true }}
              onConfirm={() => revoke(grant.agent_grants__id)}
            >
              <Button
                danger
                size='small'
                loading={revokingId === grant.agent_grants__id}
              >
                Revoke
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      )
    }
  ]

  return (
    <>
      <Alert
        showIcon
        type='info'
        style={{ marginBottom: 16 }}
        message='Agents act on your behalf'
        description='A connected agent reads Reservoir data as you, within the capabilities you consented to when you connected it. Revoking takes effect within minutes — no new agent sessions start, and live sessions expire with their short-lived token.'
      />

      {/* Read-only table inside a FormSection, per the house pattern
          (CommitmentsTab precedent) — a Table floating outside any section is
          the thing that pattern exists to prevent. The header action is the
          route back to the connect flow, which matters most for the user whose
          only grant has lapsed: Renew is on the row, but connecting afresh is
          on the other screen. */}
      <FormSection
        title='Your connections'
        titleColor='blue'
        scrollable={false}
        headerActions={
          <>
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => navigate('/config-connect-agent')}
            >
              Connect an agent
            </button>
            <HelpButton articleKey='agent_access.my_agents' variant='section' />
          </>
        }
      >
        <Spin spinning={loading}>
          {grants.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='No agents connected yet. Open Configuration → Connect an Agent for the connection URL and the steps — once your assistant is connected it will appear here.'
            />
          ) : (
            <Table
              dataSource={grants}
              columns={columns}
              rowKey='agent_grants__id'
              pagination={false}
              size='middle'
            />
          )}
        </Spin>
      </FormSection>

      <EditGrantModal
        open={editing !== null}
        saving={savingId !== null}
        agentAccess={agentAccess}
        grant={editing}
        onCancel={() => setEditing(null)}
        onSave={updateGrant}
      />
    </>
  )
}
